import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { MIRO_BOARD_SERVER } from "./accesses.ts";
import { MiroAiIconUrl } from "./icon.ts";
import { miroAiMcpTools } from "./mcp-tools.ts";
import { MIRO_AI_BOARD_ROLES } from "./roles.ts";
import { MIRO_AI_BLOCKED_TOOLS } from "./tools.ts";

/** The resolver shape of agentic's `McpServerSpecResolver`, restated here:
 * miro-ai-shared can't import agentic-shared, which imports it for the MCP
 * registry. */
type MiroAiMcpServerSpecResolver = (location: {
  gateway: string;
  server: string;
}) => McpServerSpec & { type: "external" };

// Miro applies a board role at once: in V1 (INT-1105) a new member opened the
// board right after the POST, and an open tab switched role without a
// refresh. The wait is for the gateway's entitlement rows.
const miroAiPropagationHint = propagationHint({
  maxWait: "1 minute",
  normalWait: "a few seconds",
  pollInterval: "5 seconds",
  resource: "Miro",
});

const accessPrompt = `Request a role on one Miro board by calling with argv:

  [<team>, <board>, <role>, "--reason", <reason>]

where:

  - team: The Miro team, by its identifier in P0. Use the list tool to
    enumerate them.
  - board: The board's ID, as in its URL (/app/board/<id>/). Use the list
    tool, passing the team, to enumerate the boards P0 can share.
  - role: ${MIRO_AI_BOARD_ROLES.join(", ")}, weakest first. Request the
    weakest role the work needs: viewer to read, commenter to also comment,
    editor to also change the board.
  - reason: The reason access is needed

P0 gives the role to the Miro user of the person you act for, not to you:
you reach Miro as that person, through their own sign-in to this server. So
you can also reach every board they can already open, and Miro, not P0,
decides what each call may do. Miro refuses a write on a board where they
are a viewer.

On every board tool call, pass the request ID of the grant, and the board's
full URL as the tool's miro_url argument: https://miro.com/app/board/<id>/.
P0 never grants these tools, which share boards or reach across the team:
${MIRO_AI_BLOCKED_TOOLS.join(", ")}.

If no board is listed, or a tool can't find the board, the person you act for
may have signed in to this server as another Miro user: ask them to sign in
again as themselves.
`;

/** "Miro (board access)": Miro's hosted MCP server, reached as the requester
 * through their own OAuth sign-in. A grant adds the requester's Miro user to
 * one board and writes one gateway entitlement per granted tool, so the
 * gateway refuses the sharing and cross-board tools that no grant gives.
 *
 * Beside the predefined `miro` server, at the same URL: that one takes
 * `agentic tool` requests by tool name, where an agent could request
 * `board_share` (spec section 11, decision 1). For an OAuth server the gateway
 * shows agents Miro's own `tools/list`, and this static table is its fallback
 * and what P0 lists and grants.
 */
export const miroAiMcpResolver: MiroAiMcpServerSpecResolver = (location) => {
  const prefixArgs = ["miro-ai", "board", location.gateway, location.server];
  // `ls miro-ai board` takes each listable parameter as a subcommand and the
  // others only as options, so the gateway and server can't come first here.
  const listPrefixArgs = ["miro-ai", "board"];
  return {
    access: {
      prefixArgs,
      prompt: accessPrompt,
      propagationHint: miroAiPropagationHint,
    },
    // The gateway checks each call against the request's per-tool
    // entitlements, and Miro checks the board role.
    authorization: "tool",
    // P0 doesn't check which Miro user signs in (ticket 02, decision 4).
    credential: { grant: { type: "authorization_code" }, type: "oauth" },
    iconUrl: MiroAiIconUrl,
    label: "Miro (board access)",
    list: {
      fields: {
        team: { description: "Miro team, by its P0 identifier", required: [] },
        board: { description: "Miro board ID", required: ["team"] },
      },
      prefixArgs: listPrefixArgs,
    },
    tools: { tools: miroAiMcpTools, type: "manual" },
    type: "external",
    // Root, not `/mcp`, as the predefined `miro` server's URL is: Miro serves
    // its protected-resource metadata only at the root (ENG-8593).
    url: "https://mcp.miro.com/",
  };
};

/** Whether {@link miroAiMcpResolver} can be added: only once a Miro team is
 * installed, since without one there is nothing an agent could request. */
export const miroAiMcpServerAvailability = {
  [MIRO_BOARD_SERVER]: (installed: Record<string, NewConfig>): boolean =>
    installedIds("team", { team: installed["miro-ai"]?.team ?? {} }).length > 0,
};
